import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Download, Plus, RefreshCw, RotateCcw, Trash2, X } from 'lucide-react';
import {
  startupDataService,
  type StartupDataEntity,
  type StartupDataRow,
  type StartupDataSettings,
} from '@ideoz/core';
import { Button, ConfirmDialog, cn, toastError, toastSuccess } from '@ideoz/ui';
import { columnsFor, displayValue, isEditable, typedValue, usedByNames } from './cells';
import { StartupUsersPanel } from './StartupUsersPanel';
import { StartupDataSettingsPanel } from './StartupDataSettingsPanel';

export interface StartupDataViewProps {
  projectId: string;
  /**
   * `prototype`: the Data view in the workbench, with a "This prototype | All startup data" switch.
   * `startup`: the startup's Data page, always all data, plus users and roles and settings.
   */
  mode: 'prototype' | 'startup';
  /** The prototype open in the workbench (`chef_project_links.id`), for "This prototype". */
  prototypeId?: string | null;
  /** Called after a change, so the preview shows it (`notifyAppDataChanged`). */
  onChanged?: (entity: string | null) => void;
  className?: string;
  testId?: string;
}

type Section = 'data' | 'users' | 'settings';

/**
 * A startup's shared data for its founder: every entity, which prototypes use it, and its
 * records to add, edit and delete (User Stories 596 and 598). Changes are live at once for
 * everyone using the startup's links; deletes go to "Recently deleted" for 30 days.
 */
export function StartupDataView({
  projectId,
  mode,
  prototypeId,
  onChanged,
  className,
  testId,
}: StartupDataViewProps): React.ReactElement {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>('data');
  const [entities, setEntities] = useState<StartupDataEntity[] | null>(null);
  const [settings, setSettings] = useState<StartupDataSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scope, setScope] = useState<'prototype' | 'all'>(
    mode === 'prototype' ? 'prototype' : 'all'
  );
  const [selected, setSelected] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const overview = await startupDataService.overview(projectId);
      setEntities(overview.entities);
      setSettings(overview.settings);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : t('startupData.loadFailed'));
    }
  }, [projectId, t]);

  useEffect(() => {
    setEntities(null);
    setSelected(null);
    void refresh();
  }, [refresh]);

  // Everything here: the entity list and the records of the open entity, whose panel reloads
  // when `reloadKey` changes.
  const [reloadKey, setReloadKey] = useState(0);
  const refreshAll = useCallback(() => {
    setReloadKey((key) => key + 1);
    void refresh();
  }, [refresh]);

  // A prototype writes to this data while the founder is looking elsewhere - its first run seeds
  // the startup, and every visitor's change lands here too - and this view used to answer with
  // whatever it read when it opened. On testing (23 Sep 2026) that showed an empty table for a
  // prototype whose records already existed. Coming back to the tab now reads it again.
  useEffect(() => {
    const again = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [refreshAll]);

  const usedHere = useCallback(
    (e: StartupDataEntity) => !!prototypeId && e.usedBy.some((p) => p.id === prototypeId),
    [prototypeId]
  );
  const shown = useMemo(() => {
    const all = entities ?? [];
    return scope === 'prototype' ? all.filter(usedHere) : all;
  }, [entities, scope, usedHere]);

  // Keep a selection that is still shown; otherwise pick the first entity.
  useEffect(() => {
    if (selected && shown.some((e) => e.entity === selected)) return;
    setSelected(shown[0]?.entity ?? null);
  }, [shown, selected]);

  const changed = useCallback(
    (entity: string | null) => {
      onChanged?.(entity);
      void refresh();
    },
    [onChanged, refresh]
  );

  const current = shown.find((e) => e.entity === selected) ?? null;

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col bg-background text-foreground', className)}
      data-testid={testId}
    >
      <header className="flex flex-wrap items-center gap-3 border-b border-border/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-semibold">{t('startupData.title')}</h2>
        </div>
        {mode === 'prototype' ? (
          <div
            role="radiogroup"
            aria-label={t('startupData.whichData')}
            className="inline-flex rounded-md border border-border/60 p-0.5 text-xs"
          >
            {(['prototype', 'all'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={scope === value}
                onClick={() => setScope(value)}
                className={cn(
                  'rounded px-2.5 py-1 transition-colors',
                  scope === value
                    ? 'bg-muted font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {value === 'prototype' ? t('startupData.thisPrototype') : t('startupData.allData')}
              </button>
            ))}
          </div>
        ) : (
          <div
            role="tablist"
            aria-label={t('startupData.sections')}
            className="inline-flex rounded-md border border-border/60 p-0.5 text-xs"
          >
            {(
              [
                ['data', t('startupData.records')],
                ['users', t('startupData.usersAndRoles')],
                ['settings', t('startupData.settings')],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={section === value}
                onClick={() => setSection(value)}
                className={cn(
                  'rounded px-2.5 py-1 transition-colors',
                  section === value
                    ? 'bg-muted font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={refreshAll}
          aria-label={t('startupData.refresh')}
          title={t('startupData.refresh')}
          data-testid="startup-data-refresh"
          className="ms-auto rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <RefreshCw size={14} aria-hidden />
        </button>
      </header>
      <p className="border-b border-border/60 bg-amber-50 px-5 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        {t('startupData.liveLine')}
      </p>

      {loadError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-muted-foreground">{loadError}</p>
          <Button size="sm" variant="outline" onClick={() => void refresh()}>
            {t('startupData.tryAgain')}
          </Button>
        </div>
      ) : entities === null ? (
        <p className="p-6 text-sm text-muted-foreground">{t('startupData.loading')}</p>
      ) : section === 'users' ? (
        <StartupUsersPanel projectId={projectId} onChanged={() => changed('User')} />
      ) : section === 'settings' ? (
        <StartupDataSettingsPanel projectId={projectId} settings={settings} onSaved={setSettings} />
      ) : (
        <div className="flex min-h-0 flex-1">
          <nav
            aria-label={t('startupData.kinds')}
            className="w-56 shrink-0 overflow-y-auto border-r border-border/60 py-2"
          >
            {shown.length === 0 ? (
              <div className="space-y-2 px-4 py-3 text-xs text-muted-foreground">
                <p>
                  {scope === 'prototype'
                    ? t('startupData.noneForPrototype')
                    : t('startupData.noneForStartup')}
                </p>
                {scope === 'prototype' && (entities?.length ?? 0) > 0 ? (
                  <button type="button" className="underline" onClick={() => setScope('all')}>
                    {t('startupData.showAll')}
                  </button>
                ) : null}
              </div>
            ) : (
              <ul>
                {shown.map((e) => (
                  <li key={e.entity}>
                    <button
                      type="button"
                      onClick={() => setSelected(e.entity)}
                      aria-current={e.entity === selected ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm',
                        e.entity === selected ? 'bg-muted font-medium' : 'hover:bg-muted/50'
                      )}
                    >
                      <span className="truncate">{e.entity}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {e.records}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>
          <section className="min-w-0 flex-1 overflow-auto">
            {current ? (
              <EntityRecords
                key={current.entity}
                projectId={projectId}
                entity={current}
                onChanged={changed}
                reloadKey={reloadKey}
              />
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}

function EntityRecords({
  projectId,
  entity,
  onChanged,
  reloadKey,
}: {
  projectId: string;
  entity: StartupDataEntity;
  onChanged: (entity: string | null) => void;
  /** Changes when the view is refreshed, which reloads these records. */
  reloadKey: number;
}): React.ReactElement {
  const { t } = useTranslation();
  const [rows, setRows] = useState<StartupDataRow[] | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = entity.entity;

  const load = useCallback(async () => {
    try {
      setRows(await startupDataService.records(projectId, name, showDeleted));
    } catch (error) {
      toastError(error instanceof Error ? error.message : t('startupData.loadRecordsFailed'));
      setRows([]);
    }
  }, [projectId, name, showDeleted, t]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  // A refresh keeps the rows on screen while it reads them again, so the table does not blink.
  useEffect(() => {
    if (reloadKey > 0) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const records = useMemo(() => (rows ?? []).map((r) => r.record), [rows]);
  const columns = useMemo(() => columnsFor(entity.fields, records), [entity.fields, records]);
  const fieldOf = (column: string) => entity.fields.find((f) => f.name === column);

  const run = async (work: () => Promise<void>, done?: string) => {
    setBusy(true);
    try {
      await work();
      if (done) toastSuccess(done);
      onChanged(name);
      await load();
    } catch (error) {
      toastError(error instanceof Error ? error.message : t('startupData.genericError'));
    } finally {
      setBusy(false);
    }
  };

  const typeError = (kind: 'number' | 'boolean', field: string) =>
    t(kind === 'number' ? 'startupData.fieldNumber' : 'startupData.fieldBoolean', { field });

  const saveCell = (row: StartupDataRow, column: string, text: string) => {
    const typed = typedValue(text, row.record[column], fieldOf(column));
    if ('error' in typed) {
      toastError(typeError(typed.error, column));
      return;
    }
    if (typed.value === row.record[column]) return;
    void run(() =>
      startupDataService
        .save(projectId, name, { [column]: typed.value }, row.record.id)
        .then(() => undefined)
    );
  };

  const exportCsv = async () => {
    try {
      const blob = await startupDataService.exportCsv(projectId, name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error instanceof Error ? error.message : t('startupData.exportFailed'));
    }
  };

  const notLive = !entity.seeded && entity.records === 0;

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{name}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {entity.usedBy.length > 0
              ? t('startupData.usedBy', {
                  names: usedByNames(
                    entity.usedBy.map((p) => p.title || t('startupData.untitledPrototype'))
                  ),
                })
              : t('startupData.notUsed')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!showDeleted ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAdding(true)}
              // Records added before the data is live would stop the prototype's sample rows
              // from being written on first publish (Nest refuses them too).
              disabled={busy || adding || notLive}
              title={notLive ? t('startupData.notLive') : undefined}
            >
              <Plus size={14} className="mr-1" aria-hidden />
              {t('startupData.addRecord')}
            </Button>
          ) : null}
          {!showDeleted && entity.samples > 0 ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmClear(true)}
              disabled={busy}
            >
              {t('startupData.clearSamples')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={busy || entity.records === 0}
          >
            <Download size={14} className="mr-1" aria-hidden />
            {t('startupData.exportCsv')}
          </Button>
          <Button
            size="sm"
            variant={showDeleted ? 'default' : 'ghost'}
            onClick={() => setShowDeleted((v) => !v)}
            aria-pressed={showDeleted}
          >
            {entity.deleted > 0
              ? t('startupData.recentlyDeletedCount', { count: entity.deleted })
              : t('startupData.recentlyDeleted')}
          </Button>
        </div>
      </div>

      {notLive ? (
        <p className="mx-5 mt-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {t('startupData.notLive')}
        </p>
      ) : null}

      <div className="mt-3 overflow-x-auto px-5 pb-6">
        {rows === null ? (
          <p className="py-4 text-sm text-muted-foreground">{t('startupData.loadingRecords')}</p>
        ) : (
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
                {columns.map((c) => (
                  <th key={c} scope="col" className="whitespace-nowrap px-2 py-2 font-medium">
                    {c === 'updatedAt'
                      ? t(showDeleted ? 'startupData.deletedColumn' : 'startupData.updatedColumn')
                      : c}
                  </th>
                ))}
                <th scope="col" className="px-2 py-2">
                  <span className="sr-only">{t('startupData.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {adding ? (
                <NewRecordRow
                  columns={columns}
                  onCancel={() => setAdding(false)}
                  onSave={(values) =>
                    void run(async () => {
                      const record: Record<string, unknown> = {};
                      for (const [column, text] of Object.entries(values)) {
                        if (text === '') continue;
                        const typed = typedValue(text, undefined, fieldOf(column));
                        if ('error' in typed) throw new Error(typeError(typed.error, column));
                        record[column] = typed.value;
                      }
                      await startupDataService.save(projectId, name, record);
                      setAdding(false);
                    }, t('startupData.recordAdded'))
                  }
                />
              ) : null}
              {rows.length === 0 && !adding ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-2 py-6 text-center text-sm text-muted-foreground"
                  >
                    {showDeleted ? t('startupData.noneDeleted') : t('startupData.noRecords')}
                  </td>
                </tr>
              ) : null}
              {rows.map((row) => (
                <tr
                  key={row.record.id}
                  className="border-b border-border/40 align-top hover:bg-muted/30"
                >
                  {columns.map((c) => (
                    <td key={c} className="px-2 py-1.5">
                      {c === 'id' ? (
                        <span className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                          {row.record.id}
                          {row.sample ? (
                            <span className="rounded bg-muted px-1.5 py-0.5 font-sans text-[10px] uppercase tracking-wide">
                              {t('startupData.sample')}
                            </span>
                          ) : null}
                        </span>
                      ) : c === 'updatedAt' ? (
                        <span className="whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(
                            showDeleted ? (row.deletedAt ?? row.updatedAt) : row.updatedAt
                          ).toLocaleString()}
                        </span>
                      ) : !showDeleted && isEditable(c, row.record[c]) ? (
                        <EditableCell
                          value={displayValue(row.record[c])}
                          label={t('startupData.cellLabel', { column: c, id: row.record.id })}
                          disabled={busy}
                          onSave={(text) => saveCell(row, c, text)}
                        />
                      ) : (
                        <span
                          className="block max-w-[260px] truncate text-xs"
                          title={displayValue(row.record[c])}
                        >
                          {displayValue(row.record[c])}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right">
                    {showDeleted ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () => startupDataService.restore(projectId, name, row.record.id),
                            t('startupData.recordRestored')
                          )
                        }
                      >
                        <RotateCcw size={14} className="mr-1" aria-hidden />
                        {t('startupData.restore')}
                      </Button>
                    ) : (
                      <button
                        type="button"
                        aria-label={t('startupData.deleteRecord', { id: row.record.id })}
                        disabled={busy}
                        className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                        onClick={() =>
                          void run(
                            () => startupDataService.remove(projectId, name, row.record.id),
                            t('startupData.recordDeleted')
                          )
                        }
                      >
                        <Trash2 size={14} aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={confirmClear}
        title={t('startupData.clearTitle')}
        description={t('startupData.clearDescription', { entity: name, count: entity.samples })}
        confirmLabel={t('startupData.clearSamples')}
        variant="danger"
        isLoading={busy}
        onCancel={() => setConfirmClear(false)}
        onConfirm={() =>
          run(async () => {
            const cleared = await startupDataService.clearSamples(projectId, name);
            setConfirmClear(false);
            toastSuccess(t('startupData.cleared', { count: cleared }));
          })
        }
      />
    </div>
  );
}

/** A cell that turns into a text box on click; Enter or leaving it saves, Escape cancels. */
function EditableCell({
  value,
  label,
  disabled,
  onSave,
}: {
  value: string;
  label: string;
  disabled?: boolean;
  onSave: (text: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={t('startupData.editCell', { label })}
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="block w-full max-w-[260px] truncate rounded px-1 py-0.5 text-left hover:bg-muted"
        title={value}
      >
        {value || <span className="text-muted-foreground">—</span>}
      </button>
    );
  }
  const finish = (save: boolean) => {
    setEditing(false);
    if (save && draft !== value) onSave(draft);
    else setDraft(value);
  };
  return (
    <input
      aria-label={label}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish(true);
        if (e.key === 'Escape') finish(false);
      }}
      className="w-full min-w-[120px] rounded border border-border bg-background px-1 py-0.5 text-sm outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

function NewRecordRow({
  columns,
  onSave,
  onCancel,
}: {
  columns: string[];
  onSave: (values: Record<string, string>) => void;
  onCancel: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const editable = columns.filter((c) => c !== 'updatedAt');
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <tr className="border-b border-border/60 bg-muted/30">
      {columns.map((c) => (
        <td key={c} className="px-2 py-1.5">
          {editable.includes(c) ? (
            <input
              aria-label={t('startupData.newRecordField', { column: c })}
              placeholder={c === 'id' ? t('startupData.idPlaceholder') : c}
              value={values[c] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [c]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSave(values);
                if (e.key === 'Escape') onCancel();
              }}
              className="w-full min-w-[100px] rounded border border-border bg-background px-1 py-0.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          ) : null}
        </td>
      ))}
      <td className="whitespace-nowrap px-2 py-1.5 text-right">
        <Button size="sm" onClick={() => onSave(values)}>
          {t('startupData.save')}
        </Button>
        <button
          type="button"
          aria-label={t('startupData.cancelNew')}
          onClick={onCancel}
          className="ml-1 rounded p-1 hover:bg-muted"
        >
          <X size={14} aria-hidden />
        </button>
      </td>
    </tr>
  );
}

export default StartupDataView;
